import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OutsideClickHandler from "react-outside-click-handler";
import ProfileMenu from "../../components/ProfileMenu";
import { Images } from "../../assets/common";
import ApiService from "../../services/api-service";
import { HMS } from "../../constants/function";
import { useDispatch } from "react-redux";
import { addSongsAction } from "../../action/songs";
import { useTranslation } from "react-i18next";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import { useHistory } from "react-router-dom";

export default function MyMusicTab({ userId }) {
  const [myMusic, setMyMusic] = useState([]);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [showStudioPopup, setShowStudioPopup] = useState(false);
  const [studioMusicId, setStudioMusicId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [musicToDelete, setMusicToDelete] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setLoadingMusic(true);
    ApiService.MY_MUSIC(`assetId=${userId}&type=user`)
      .then(res => setMyMusic(res.result || []))
      .finally(() => setLoadingMusic(false));
  }, [userId]);

  // Menu actions for each row
  const getMenuActions = (m) => [
    { key: "info", action: "View Song Information" },
    { key: "add_to_studio", action: "Add to Studio" },
    {
      key: m.private ? "mark_public" : "mark_private",
      action: m.private ? "Mark Public" : "Mark Private"
    },
    { key: "delete", action: "Delete Song" }
  ];

 const handleMenuAction = async (key, musicId, idx) => {
  setOpen(false);
  setSelectedIndex(null);

  if (key === "info") {
    // If NFT ID exists, go to NFT details, else go to song details
    const nftId = myMusic[idx]?.nftId;
    if (nftId) {
      history.push(`/app/SongDetail/${nftId}`);
    } else {
      history.push(`/app/SongDetails/${musicId}`);
    }
  } else if (key === "add_to_studio") {
    setStudioMusicId(musicId);
    setShowStudioPopup(true);
  } else if (key === "mark_private" || key === "mark_public") {
    try {
      setLoadingMusic(true);
      await ApiService.ASSET_PRIVACY({
        assetId: musicId,
        type: "music",
        private: !myMusic[idx].private,
      });
      const updated = [...myMusic];
      updated[idx].private = !updated[idx].private;
      setMyMusic(updated);
      setLoadingMusic(false);
    } catch (error) {
      setLoadingMusic(false);
      alert("Failed to update privacy");
    }
  } else if (key === "delete") {
    setMusicToDelete({ musicId, idx, title: myMusic[idx].title });
    setDeleteDialogOpen(true);
  }
};

const handleDeleteConfirm = async () => {
  if (!musicToDelete) return;

  try {
    setLoadingMusic(true);
    setDeleteDialogOpen(false);

    await ApiService.DELETE_MUSIC(musicToDelete.musicId);

    // Remove from local state
    const updated = myMusic.filter((_, i) => i !== musicToDelete.idx);
    setMyMusic(updated);
    setMusicToDelete(null);
    setLoadingMusic(false);

    alert("Song deleted successfully");
  } catch (error) {
    setLoadingMusic(false);
    const errorMsg = error.response?.data?.error || error.message || "Failed to delete song";
    alert(errorMsg);
    console.error("Delete error:", error);
  }
};

const handleDeleteCancel = () => {
  setDeleteDialogOpen(false);
  setMusicToDelete(null);
};
  return (
    <>
      <Grid container spacing={2} sx={{
        color: "var(--text-primary, #fff)",
        mb: 1,
        px: 1,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        fontSize: 15,
        borderBottom: "1px solid var(--accent-color, #232323)",
        pb: 1.5,
      }}>
        <Grid item xs={4}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Title</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Listens</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Private</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>Duration</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle2" align="center" sx={{ fontWeight: 700, color: "var(--text-primary, #fff)" }}>
            Actions
          </Typography>
        </Grid>
      </Grid>
      <Box>
        {loadingMusic ? (
          <Typography color="var(--text-primary, #fff)">Loading...</Typography>
        ) : myMusic.length === 0 ? (
          <Typography color="var(--text-primary, #fff)">No music found.</Typography>
        ) : (
          myMusic.map((m, idx) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={m._id || idx}
              sx={{
                background: "var(--accent-color, #1a1a1a)",
                borderRadius: "12px",
                mb: 3,
                px: 1,
                paddingTop: 1,
                paddingBottom: 2.5,
                "&:hover": { background: "var(--background-color, #232323)" },
              }}
            >
              <Grid item xs={4}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() => dispatch(addSongsAction(myMusic, false, null, idx))}
                >
                  <Box
                    component="img"
                    src={m.thumbnailImg?.filePath || Images.circle_music}
                    alt={m.title}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      height: 48,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="var(--text-primary, #fff)" fontWeight={700}>
                      {m.title}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="var(--primary-color, #ffea00)"
                      fontWeight={600}
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = `/app/artistprofile/${m.artistData?.[0]?.uId || ""}`;
                      }}
                    >
                      {m.artistData && m.artistData.length
                        ? m.artistData.map(a => a.displayArtistName).join(", ")
                        : m.artist || ""}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Typography color="var(--text-primary, #fff)" fontWeight={500}>
                  {m.listenCount ?? m.listens ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color={m.private ? "var(--text-primary, #fff)" : "var(--success-color, #8fff8f)"} fontWeight={500}>
                  {m.private ? "Yes" : "No"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography color="var(--text-primary, #fff)" fontWeight={500}>
                  {HMS(m.duration)}
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: "center", position: "relative" }}>
                <IconButton
                  aria-label="more"
                  onClick={() => {
                    setSelectedIndex(idx);
                    setOpen(idx);
                  }}
                  sx={{ color: "var(--text-primary, #fff)" }}
                >
                  <MoreVertIcon className="__more_icon" />
                </IconButton>
                {selectedIndex === idx && open === idx && (
                  <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
                    <ProfileMenu
                      isLast={
                        idx === myMusic.length - 1 && myMusic.length > 2
                          ? "-90px !important"
                          : "40px"
                      }
                      right={"0px"}
                      isMenu={open === idx}
                      actions={getMenuActions(m)}
                      handleProfileMenuChoice={key => handleMenuAction(key, m._id, idx)}
                    />
                  </OutsideClickHandler>
                )}
              </Grid>
            </Grid>
          ))
        )}
      </Box>
      {/* Studio/Playlist popup */}
      <AddMusicToPlaylist
        musicId={studioMusicId}
        isAlbum={false}
        open={showStudioPopup}
        handleClose={() => setShowStudioPopup(false)}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            background: "var(--accent-color, #1a1a1a)",
            color: "var(--text-primary, #fff)",
            borderRadius: "12px",
            minWidth: "400px"
          }
        }}
      >
        <DialogTitle sx={{ color: "var(--text-primary, #fff)", fontWeight: 700 }}>
          Delete Song
        </DialogTitle>
        <DialogContent>
          <Typography color="var(--text-primary, #fff)">
            Are you sure you want to delete "{musicToDelete?.title}"?
          </Typography>
          <Typography color="var(--text-secondary, #999)" sx={{ mt: 2, fontSize: "0.9rem" }}>
            Note: You can only delete songs if you own all NFT collectibles.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: "var(--text-primary, #fff)",
              textTransform: "none"
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              background: "var(--error-color, #ff4444)",
              color: "#fff",
              textTransform: "none",
              "&:hover": {
                background: "var(--error-color-dark, #cc0000)"
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
